import { useCallback, useEffect, useState } from "react";
import { Pencil } from "lucide-react";

import { businessApi } from "../../../../services/api";
import {
  Button, ConfirmDialog, ErrorState, Field, Input, Notice, Select, SkeletonRows, Surface, Textarea, cn, focusRing, plainError,
} from "../../../../components/ui";
import SettingsRelatedLinks from "./SettingsRelatedLinks";

// Report 57: how lateness and leaving early change pay. The server decides and stores every amount; this panel shows
// the rules in force, the change waiting to start, and lets the owner (the employer) set a new version from a date.

const TREATMENT_WORD = {
  STANDARD: "Unpaid time is taken from pay",
  PAID: "Paid anyway",
};
const TREATMENT_HINT = {
  STANDARD: "In proportion to the day's pay: the unpaid minutes × (the day's pay ÷ the shift's paid hours).",
  PAID: "Nothing is taken. Use this only when the employment terms say so.",
};

const longDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
const fromText = (rules) => (rules.fromTheStart ? "Since the business started using Apparify" : `From ${longDate(rules.effectiveFrom)}`);
// Report 58: the seeded version's note was written for engineers; it is shown in plain words.
const noteText = (rules) => (rules.source === "SEEDED" ? "Apparify's standard rules." : rules.basisNote);
const minutes = (value) => `${value} minute${Number(value) === 1 ? "" : "s"}`;

export function graceSentence(grace) {
  return Number(grace) === 0
    ? "There is no grace: clocking in after the shift starts is late."
    : `Clocking in up to ${minutes(grace)} after the shift starts is on time and paid. Later than that is late from the shift start (9:00 start, ${grace} min grace: 9:${String(Number(grace) + 1).padStart(2, "0")} is ${Number(grace) + 1} min late).`;
}

/** Field checks before anything is sent; the server checks the same and more. */
export function attendanceRulesErrors(form, { maxGraceMinutes = 60 } = {}) {
  const errors = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(form.effectiveFrom || ""))) errors.effectiveFrom = "Choose the date the rules start.";
  const grace = String(form.graceMinutes ?? "").trim();
  if (!/^\d+$/.test(grace) || Number(grace) > maxGraceMinutes) errors.graceMinutes = `Enter 0 to ${maxGraceMinutes} whole minutes.`;
  if (!TREATMENT_WORD[form.latenessTreatment]) errors.latenessTreatment = "Choose how lateness is paid.";
  if (!TREATMENT_WORD[form.undertimeTreatment]) errors.undertimeTreatment = "Choose how leaving early is paid.";
  const note = String(form.basisNote || "").trim();
  if (note.length < 10) errors.basisNote = "Say why, or where the rule is written (at least 10 characters).";
  else if (note.length > 500) errors.basisNote = "Keep it to 500 characters.";
  return errors;
}

function RuleFacts({ rules }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
      <dt className="text-meta text-secondary">Grace</dt>
      <dd className="text-body text-foreground">
        {minutes(rules.graceMinutes)}
        <span className="block text-body-sm text-secondary">{graceSentence(rules.graceMinutes)}</span>
      </dd>
      <dt className="text-meta text-secondary">Late after the grace</dt>
      <dd className="text-body text-foreground">
        {TREATMENT_WORD[rules.latenessTreatment]}
        <span className="block text-body-sm text-secondary">{TREATMENT_HINT[rules.latenessTreatment]}</span>
      </dd>
      <dt className="text-meta text-secondary">Leaving early</dt>
      <dd className="text-body text-foreground">
        {TREATMENT_WORD[rules.undertimeTreatment]}
        <span className="block text-body-sm text-secondary">{TREATMENT_HINT[rules.undertimeTreatment]}</span>
      </dd>
      <dt className="text-meta text-secondary">In force</dt>
      <dd className="text-body text-foreground">{fromText(rules)}</dd>
      <dt className="text-meta text-secondary">Reason or reference</dt>
      <dd className="text-body-sm text-foreground">{noteText(rules) || <span className="text-tertiary">Not recorded</span>}</dd>
    </dl>
  );
}

function ChangeForm({ rules, onCancel, onSaved }) {
  const [form, setForm] = useState(() => ({
    effectiveFrom: "",
    graceMinutes: String(rules.current?.graceMinutes ?? 15),
    latenessTreatment: rules.current?.latenessTreatment || "STANDARD",
    undertimeTreatment: rules.current?.undertimeTreatment || "STANDARD",
    basisNote: "",
  }));
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const set = (key) => (event) => { setForm((current) => ({ ...current, [key]: event.target.value })); setErrors((current) => ({ ...current, [key]: undefined })); };

  const check = () => {
    const found = attendanceRulesErrors(form, { maxGraceMinutes: rules.maxGraceMinutes });
    setErrors(found);
    if (!Object.keys(found).length) setReview(true);
  };

  const save = async () => {
    const response = await businessApi.recordHRAttendancePayRules({
      effectiveFrom: form.effectiveFrom,
      graceMinutes: Number(form.graceMinutes),
      latenessTreatment: form.latenessTreatment,
      undertimeTreatment: form.undertimeTreatment,
      basisNote: form.basisNote.trim(),
      confirm: true,
    });
    setReview(false);
    onSaved(response.rules);
  };

  return (
    <div className="mt-4 space-y-4 border-t border-subtle pt-4">
      <h3 className="text-panel-title text-foreground">Change the attendance pay rules</h3>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Applies from" required error={errors.effectiveFrom} hint="Payroll for dates from this day uses the new rules. A payroll already with Finance or issued is never changed.">
          <Input type="date" value={form.effectiveFrom} min={rules.current && !rules.current.fromTheStart ? rules.current.effectiveFrom : undefined} onChange={set("effectiveFrom")} />
        </Field>
        <Field label="Grace (minutes)" required error={errors.graceMinutes} hint={`0 to ${rules.maxGraceMinutes} minutes. It applies to every shift except the older shifts listed below, which still carry their own grace.`}>
          <Input type="number" inputMode="numeric" min="0" max={rules.maxGraceMinutes} step="1" value={form.graceMinutes} onChange={set("graceMinutes")} />
        </Field>
        <Field label="Late after the grace" required error={errors.latenessTreatment} hint={TREATMENT_HINT[form.latenessTreatment]}>
          <Select value={form.latenessTreatment} onChange={set("latenessTreatment")}>
            <option value="STANDARD">{TREATMENT_WORD.STANDARD} (standard)</option>
            <option value="PAID">{TREATMENT_WORD.PAID}</option>
          </Select>
        </Field>
        <Field label="Leaving early" required error={errors.undertimeTreatment} hint={TREATMENT_HINT[form.undertimeTreatment]}>
          <Select value={form.undertimeTreatment} onChange={set("undertimeTreatment")}>
            <option value="STANDARD">{TREATMENT_WORD.STANDARD} (standard)</option>
            <option value="PAID">{TREATMENT_WORD.PAID}</option>
          </Select>
        </Field>
      </div>
      <Field label="Reason or reference" required error={errors.basisNote} hint="For example the handbook section or the agreement with staff. 10 to 500 characters.">
        <Textarea rows={2} maxLength={500} value={form.basisNote} onChange={set("basisNote")} />
      </Field>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={check}>Review change</Button>
      </div>
      {review ? (
        <ConfirmDialog
          size="md"
          title={`Change the attendance pay rules from ${longDate(form.effectiveFrom)}?`}
          facts={[
            { label: "Grace", value: `${minutes(rules.current?.graceMinutes ?? 0)} → ${minutes(Number(form.graceMinutes))}` },
            { label: "Late after the grace", value: `${TREATMENT_WORD[rules.current?.latenessTreatment] || "-"} → ${TREATMENT_WORD[form.latenessTreatment]}` },
            { label: "Leaving early", value: `${TREATMENT_WORD[rules.current?.undertimeTreatment] || "-"} → ${TREATMENT_WORD[form.undertimeTreatment]}` },
            { label: "Reason or reference", value: form.basisNote.trim() },
          ]}
          consequence="Payroll for these dates is worked out with the new rules the next time it is calculated. Payroll already with Finance or issued is not changed. The earlier rules are kept as history."
          confirmLabel="Save attendance pay rules"
          cancelLabel="Back to editing"
          failureTitle="Not saved"
          onConfirm={save}
          onClose={() => setReview(false)}
        >
          <p className="text-body-sm text-secondary">
            You confirm, as the employer, that these rules follow your employment terms. Apparify applies them as set;
            it never takes more than the unpaid time is worth.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * Report 58 (APP-HR-142): shifts made before the grace became part of these rules may still carry their own grace. They
 * keep it (their times are fixed once used), and it is shown here so the owner sees every grace that applies.
 */
function GraceOverrides() {
  const [shifts, setShifts] = useState(null);
  useEffect(() => {
    let live = true;
    businessApi.getShifts({ status: "active" })
      .then((response) => { if (live) setShifts((response.shifts || []).filter((row) => row.grace_period_minutes !== null && row.grace_period_minutes !== undefined)); })
      .catch(() => { if (live) setShifts([]); });
    return () => { live = false; };
  }, []);
  if (!shifts || !shifts.length) return null;
  return (
    <div className="mt-4 border-t border-subtle pt-4">
      <h3 className="text-label text-secondary">Shifts with their own grace</h3>
      <p className="mt-1 max-w-[75ch] text-body-sm text-secondary">
        These older shifts keep the grace they were made with; the grace above does not apply to them. New shifts have
        no grace of their own. To use the rules above, give the employees a new shift from a date in Shifts.
      </p>
      <ul className="mt-2 divide-y divide-subtle text-body-sm">
        {shifts.map((row) => (
          <li key={row.id} className="flex justify-between gap-4 py-1.5">
            <span className="text-foreground">{row.name}</span>
            <span className="tabular-nums text-secondary">{row.grace_period_minutes} min</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AttendancePayRulesPanel() {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [result, setResult] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await businessApi.getHRAttendancePayRules();
      setRules(response.rules);
      setError("");
    } catch (err) {
      setError(plainError(err, "The attendance pay rules couldn't be loaded."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = (next, verb) => {
    setRules(next);
    setEditing(false);
    const stale = next.recalculate || [];
    setResult({
      tone: "success",
      text: `${verb}.${stale.length ? ` Recalculate ${stale.map((row) => row.name).join(", ")} so ${stale.length === 1 ? "it uses" : "they use"} the rules.` : ""}`,
    });
  };

  if (error && !rules) return <ErrorState title="Attendance pay rules couldn't be loaded" description={error} onRetry={load} />;
  if (!rules) return <SkeletonRows rows={3} columns={2} label="Loading the attendance pay rules" />;

  const scheduled = rules.scheduled || [];
  return (
    <Surface padded as="section" aria-label="Attendance pay rules">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <h2 className="text-section-title text-foreground">Attendance pay rules</h2>
        {rules.canManage && !editing ? <Button size="sm" icon={Pencil} onClick={() => { setEditing(true); setResult(null); }}>Change rules</Button> : null}
      </div>
      <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">
        How lateness and leaving early change pay. Absences, half days, leave and suspension are counted separately, and the same time is never taken twice.
      </p>

      <div className="mt-4">{rules.current ? <RuleFacts rules={rules.current} /> : <p className="text-body-sm text-secondary">No rules are on file; payroll uses Apparify's standard rules.</p>}</div>

      {scheduled.map((change) => (
        <div key={change.id} className="mt-4 rounded-panel border border-default p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
            <h3 className="text-panel-title text-foreground">Change starting {longDate(change.effectiveFrom)}</h3>
            {rules.canManage ? <Button size="sm" variant="quiet" onClick={() => setWithdrawing(change)}>Withdraw change</Button> : null}
          </div>
          <div className="mt-3"><RuleFacts rules={change} /></div>
        </div>
      ))}

      <div className="mt-4 border-t border-subtle pt-4">
        <h3 className="text-label text-secondary">How the money is worked out</h3>
        <ul className="mt-2 max-w-[75ch] list-disc space-y-1 pl-5 text-body-sm text-secondary">
          <li>Fixed salary: a day is this period's salary ÷ its working days on the employee's schedule; an hour of it is that day ÷ the shift's paid hours.</li>
          <li>Paid by the day: an hour is the daily rate ÷ the shift's paid hours.</li>
          <li>Paid by the hour: pay already counts only the time worked, so nothing more is taken. Minutes inside the grace are paid.</li>
          <li>Never more than one day's pay for a day, never on a rest day, holiday, leave day or half day, and a forgotten clock-out is never counted as leaving early.</li>
        </ul>
        <p className="mt-3 max-w-[75ch] text-meta text-tertiary">
          These rules are the employer's decision. Apparify applies them to attendance; it does not decide what your contracts or the law allow.
        </p>
      </div>

      {!rules.canManage ? <p className="mt-3 text-body-sm text-secondary">Only the business owner can change these rules.</p> : null}
      {editing ? <ChangeForm rules={rules} onCancel={() => setEditing(false)} onSaved={(next) => saved(next, "Attendance pay rules saved")} /> : null}
      {result ? <div className="mt-4"><Notice tone={result.tone} role="status">{result.text}</Notice></div> : null}

      {(rules.history || []).length > 1 ? (
        <details className="mt-4 border-t border-subtle pt-3">
          <summary className={cn("cursor-pointer rounded-control text-body-sm font-medium text-foreground max-md:min-h-touch max-md:py-2", focusRing)}>Earlier and withdrawn rules</summary>
          <ul className="mt-2 divide-y divide-subtle">
            {rules.history.map((row) => (
              <li key={row.id} className="py-2 text-body-sm">
                <span className="font-medium text-foreground">{fromText(row)}</span>
                <span className="text-secondary"> · grace {minutes(row.graceMinutes)} · late: {TREATMENT_WORD[row.latenessTreatment].toLowerCase()} · early: {TREATMENT_WORD[row.undertimeTreatment].toLowerCase()}</span>
                {row.withdrawnAt ? <span className="text-secondary"> · withdrawn</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {withdrawing ? (
        <ConfirmDialog
          tone="danger"
          title={`Withdraw the change starting ${longDate(withdrawing.effectiveFrom)}?`}
          consequence="The rules in force now keep applying from that date. The change stays in the history as withdrawn."
          confirmLabel="Withdraw change"
          cancelLabel="Keep change"
          failureTitle="Not withdrawn"
          onConfirm={async () => {
            const response = await businessApi.withdrawHRAttendancePayRules(withdrawing.id);
            setWithdrawing(null);
            saved(response.rules, "Change withdrawn");
          }}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
      <GraceOverrides />
      <SettingsRelatedLinks items={[
        { to: "/business/hr/attendance", label: "View attendance records" },
        { to: "/business/hr/shifts", label: "Manage shifts" },
      ]} />
    </Surface>
  );
}
